import React, { ReactNode } from 'react';
import { css } from '@emotion/react';
import { color, shadow, spacing, typography } from '@uniquegood/realworld-studio-design';
import { onMobile } from '@/styles/responsive';

interface CardProps {
  /** 카드의 제목 */
  title?: string | ReactNode;
  /** 카드의 설명 */
  description?: string;
  /** 카드 본문에 들어갈 컴포넌트들 */
  children?: React.ReactNode;
}

/**
 * 재사용 가능한 카드 컴포넌트
 *
 * Figma 디자인을 기반으로 구현된 반응형 카드 컴포넌트입니다.
 * 제목과 설명을 prop으로 받아서 헤더 영역에 표시하고,
 * children을 통해 본문 영역에 다양한 컴포넌트를 노출할 수 있습니다.
 *
 * @example
 * ```tsx
 * <Card
 *   title="장르는 무엇인가요?"
 *   description="전체 스토리의 방향성을 잡는 핵심 키워드를 설정해요."
 * >
 *   <Button>드라마</Button>
 *   <Button>코믹</Button>
 * </Card>
 * ```
 */
const Card: React.FC<CardProps> = ({ title, description, children }) => {
  return (
    <div css={cardContainerStyle}>
      <div css={cardContentStyle}>
        {(title || description) && (
          <div css={headerStyle}>
            {title && typeof title === 'string' && <div css={titleStyle}>{title}</div>}
            {title && typeof title !== 'string' && title}
            {description && <div css={descriptionStyle}>{description}</div>}
          </div>
        )}
        {children && <div css={bodyStyle}>{children}</div>}
      </div>
    </div>
  );
};

const cardContainerStyle = css`
  background-color: ${color.surface_default_default};
  border-radius: ${spacing.borderRadius.xlarge};
  border: 1px solid ${color.border_default_subdued};
  box-shadow: ${shadow.card};
  position: relative;
  width: 100%;
  box-shadow: 0 0 30px 0 rgba(0, 0, 0, 0.07);

  ${onMobile} {
    border-radius: ${spacing.borderRadius.large};
  }
`;

const cardContentStyle = css`
  padding: ${spacing.margin.xlarge2};
  display: flex;
  flex-direction: column;
  gap: ${spacing.common.medium};
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
  position: relative;
  width: 100%;
  height: 100%;

  ${onMobile} {
    padding: ${spacing.common.large};
    gap: ${spacing.common.medium};
  }
`;

const headerStyle = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.common.xsmall};
  align-items: flex-start;
  justify-content: flex-start;
  position: relative;
  width: 100%;

  ${onMobile} {
    gap: ${spacing.margin.xsmall};
  }
`;

const titleStyle = css`
  font-family: ${typography.family.primary};
  font-weight: ${typography.weight.bold};
  font-size: ${typography.size.s3};
  line-height: 1.5;
  color: ${color.text_default};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${onMobile} {
    font-size: ${typography.size.s2};
    white-space: normal;
  }
`;

const descriptionStyle = css`
  font-family: ${typography.family.primary};
  font-weight: ${typography.weight.regular};
  font-size: ${typography.size.s1};
  line-height: 1.33;
  color: ${color.text_subdued};
  margin: 0;
  width: 100%;

  ${onMobile} {
    font-size: ${typography.size.s1};
  }
`;

const bodyStyle = css`
  width: 100%;
  height: 100%;
`;

export default Card;  